import { cloneDeep } from 'lodash';
import {
  AccountClient,
  apiMethodAndKeyNames,
  apiResolver,
  GET_REQUESTERS_BY_CRITERIA,
  GET_TECHNICIANS_BY_CRITERIA,
  GET_FIELD,
  GET_CLIENTS,
  TicketClient
} from '@nile/graphql';
import { getTranslatedText, ALL_MODULES } from '@nile/components';

const getDefaultValue = (f, condition) => {
  const operands = cloneDeep(condition.operands);
  const ruleIdx = operands.findIndex((el) => {
    if (el.attribute?.indexOf('.') > -1) {
      const attr = el.attribute.split('.')[0];
      return attr === f.name;
    }
    return el.attribute === f.name;
  });
  if (ruleIdx > -1) {
    const rule = operands[ruleIdx];
    switch (f.dataType) {
      case 'date':
      case 'datetime':
        return rule.value;
      default:
        break;
    }
    switch (f.filterType) {
      case 'text':
      case 'numeric':
      case 'decimal':
      case 'paragraph':
      case 'read_only':
        return rule.value;
      default:
        return rule.value.map((el) => {
          return {
            name: el,
            value: el,
            label: el
          };
        });
    }
  }
  return '';
};

const checkDynamicQuery = (f) => {
  if (f.isCustom) return true;
  switch (f.name) {
    case 'client':
    case 'subcategory':
    case 'subcause':
      return true;
    default:
      return false;
  }
};

const getDynamicQuery = (f) => {
  const resolverModule = 'Ticket';
  let client = TicketClient;
  let query = apiResolver[resolverModule][f.name]?.api;
  const variables = apiResolver[resolverModule][f.name]?.variables || {};

  if (f.isCustom) {
    return AccountClient.query({
      query: GET_FIELD,
      variables: {
        input: {
          columnName: f?.name || f?.columnName,
          module: ALL_MODULES.ticket
        }
      }
    });
  }
  switch (f.name) {
    case 'client':
      client = AccountClient;
      query = GET_CLIENTS;
      return client.query({
        query: query,
        variables: {
          listInfo: {}
        }
      });
    default:
      query = apiResolver[resolverModule][f.name]?.api;
      client = apiResolver[resolverModule][f.name]?.client;
      return client.query({
        query: query,
        variables: variables
      });
  }
};

const getMethodName = (field) => {
  switch (field) {
    case 'subcategory':
      return 'getAllCategory';
    case 'subcause':
      return 'getAllCause';
    default:
      return 'name';
  }
};

const getKeyName = (field) => {
  switch (field) {
    case 'subcategory':
      return 'subCategories';
    case 'subcause':
      return 'subCauses';
    default:
      return 'name';
  }
};

const generateOptions = (f, data) => {
  const options = [];
  let allValues = [];
  if (f.isCustom) {
    const fldOptions = data?.getField?.options || data?.getField?.fieldOptions;
    allValues = fldOptions?.map((el) => {
      return {
        name: el?.value,
        value: el?.value,
        label: el?.value
      };
    });
    return allValues;
  }
  const methodName = getMethodName(f.name); // TODO: ASH Deprecate API Resolver
  const key = getKeyName(f.name);
  switch (f.name) {
    case 'client':
      allValues = data.getClientList.clients.map((el) => {
        return {
          ...el,
          name: el.accountId,
          value: el.name,
          label: el.name
        };
      });
      break;

    case 'subcategory':
    case 'subcause':
      data[methodName].forEach((el) => {
        const subValues = el[key].map((c) => {
          return {
            name: c.name,
            value: c.name,
            label: c.name
          };
        });
        allValues = allValues.concat(subValues);
      });
      break;
    default:
      allValues = [];
  }
  return allValues;
};

function getQuery(property) {
  switch (property.name) {
    case 'technician':
      return GET_TECHNICIANS_BY_CRITERIA;
    default:
      return GET_REQUESTERS_BY_CRITERIA;
  }
}

function getNameKey(property) {
  switch (property.name) {
    case 'technician':
      return 'getTechnicianList';
    default:
      return 'getRequesterList';
  }
}

const fetchValues = async (client, query, params) => {
  console.log('fetching values', client, query, params);
  const attribute = 'name';
  const nameKey = getNameKey(params.property);
  const res = await client.query({
    query: query,
    variables: {
      listInfo: {
        condition: {
          joinOperator: 'AND',
          operands: [
            {
              attribute: attribute || 'name',
              operator: 'startsWith',
              value: params.inputValue
            }
          ]
        }
      }
    }
  });
  const allOptions = res?.data?.[nameKey]
    ? res.data[nameKey].userList.map((el) => {
        return {
          name: `${el[attribute]}`,
          value: `${el[attribute]}`,
          label: `${el[attribute]}`
        };
      })
    : [];

  console.log(allOptions);
  return allOptions;
};

const getAsyncOptions = (property) => {
  return { client: AccountClient, query: getQuery(property) };
};

const getAllowedProperties = (meta, intl) => {
  const ticketMeta = meta.find((el) => el.name === 'Task');
  let allowedProperties = [];

  ticketMeta.attributes.forEach((el) => {
    switch (el.name) {
      case 'title':
      case 'estimatedTime':
      case 'scheduledStartDate':
      case 'dueDate':
      case 'techGroup':
      case 'technician':
      case 'displayId':
      case 'status':
        if (el.label) {
          allowedProperties.push({
            ...el,
            label: getTranslatedText(intl, el.label)
          });
        }
        break;
      default:
        break;
    }
  });
  allowedProperties = allowedProperties.sort((a, b) =>
    a.label.toUpperCase() > b.label.toUpperCase()
      ? 1
      : b.label.toUpperCase() > a.label.toUpperCase()
      ? -1
      : 0
  );
  return allowedProperties;
};

const getVariables = (property) => {
  switch (property.name) {
    case 'client':
      return { listInfo: {} };
    default:
      return null;
  }
};

const getDateOperators = (intl) => {
  const dateOperators = [
    {
      key: 'placeholder.today',
      label: getTranslatedText(intl, 'placeholder.today'),
      value: " placeholder.today',"
    },
    {
      key: 'placeholder.tomorrow',
      label: getTranslatedText(intl, 'placeholder.tomorrow'),
      value: " placeholder.tomorrow',"
    },
    {
      key: 'placeholder.yesterday',
      label: getTranslatedText(intl, 'placeholder.yesterday'),
      value: " placeholder.yesterday',"
    },
    {
      key: 'placeholder.last.week',
      label: getTranslatedText(intl, 'placeholder.last.week'),
      value: 'placeholder.last.week'
    },
    {
      key: 'placeholder.this.week',
      label: getTranslatedText(intl, 'placeholder.this.week'),
      value: 'placeholder.this.week'
    },
    {
      key: 'placeholder.next.week',
      label: getTranslatedText(intl, 'placeholder.next.week'),
      value: 'placeholder.next.week'
    },
    {
      key: 'placeholder.last.month',
      label: getTranslatedText(intl, 'placeholder.last.month'),
      value: 'placeholder.last.month'
    },
    {
      key: 'placeholder.this.month',
      label: getTranslatedText(intl, 'placeholder.this.month'),
      value: 'placeholder.this.month'
    },
    {
      key: 'placeholder.next.month',
      label: getTranslatedText(intl, 'placeholder.next.month'),
      value: 'placeholder.next.month'
    }
  ];
  return dateOperators;
};

const getDynamicKey = (property) => {};

export {
  getDefaultValue,
  getDynamicQuery,
  checkDynamicQuery,
  generateOptions,
  getMethodName,
  getKeyName,
  getVariables,
  getDateOperators,
  fetchValues,
  getAsyncOptions,
  getAllowedProperties
};
